// S-06 - Application & Scale Configuration (Admin) - US-02, US-04
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  configApi,
  applicationApi,
  applicationEnvironmentsApi,
  ApiRequestError,
  type ApplicationDto,
  type ApplicationEnvironmentDto,
  type ScaleLevelDto,
  type ThresholdDto,
} from "../api/api";

export function ApplicationScaleConfigPage() {
  const { t } = useTranslation();
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [scale, setScale] = useState<ScaleLevelDto[]>([]);
  const [threshold, setThreshold] = useState<ThresholdDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newApplicationLabel, setNewApplicationLabel] = useState("");
  const [thresholdValue, setThresholdValue] = useState(2);
  const [newLevel, setNewLevel] = useState({ level: 5, labelEn: "", labelFr: "" });
  const [applicationFile, setApplicationFile] = useState<File | null>(null);
  const [scaleFile, setScaleFile] = useState<File | null>(null);
  const [thrFile, setThrFile] = useState<File | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  // ─── Environments ──────────────────────────────────────────────────────────
  const [environments, setEnvironments] = useState<ApplicationEnvironmentDto[]>([]);
  const [newEnvLabel, setNewEnvLabel] = useState("");
  const [newEnvAppId, setNewEnvAppId] = useState("");

  async function load() {
    setError(null);
    try {
      const [appRes, scaleRes, thrRes] = await Promise.all([
        applicationApi.list(),
        configApi.listScale(),
        configApi.getThreshold(),
      ]);
      setApplications(appRes.applications ?? []);
      setScale(scaleRes.scale);
      setThreshold(thrRes.threshold);
      if (thrRes.threshold) setThresholdValue(thrRes.threshold.value);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    }
    // Environnements chargés séparément — ne bloque pas la page si erreur
    try {
      const envRes = await applicationEnvironmentsApi.list(undefined, true);
      setEnvironments(envRes.environments ?? []);
    } catch {
      // silencieux — la section affiche juste le formulaire d'ajout vide
    }
  }

  async function addEnvironment(e: FormEvent) {
    e.preventDefault();
    if (!newEnvAppId) { setError(t("required_application")); return; }
    try {
      await applicationEnvironmentsApi.create({ applicationId: newEnvAppId, label: newEnvLabel.trim() });
      setNewEnvLabel("");
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("create_failed"));
    }
  }

  async function toggleEnvironment(env: ApplicationEnvironmentDto) {
    try {
      await applicationEnvironmentsApi.patch(env.id, { active: !env.active });
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    }
  }

  async function deleteEnvironment(env: ApplicationEnvironmentDto) {
    if (!confirm(`${t("confirm_delete")} "${env.label}" ?`)) return;
    try {
      await applicationEnvironmentsApi.delete(env.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("delete_failed"));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function addApplication(event: FormEvent) {
    event.preventDefault();
    try {
      await applicationApi.create({ label: newApplicationLabel.trim(), sortOrder: applications.length + 1 });
      setNewApplicationLabel("");
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("create_erp_failed"));
    }
  }

  async function toggleApplication(item: ApplicationDto) {
    try {
      await applicationApi.patch(item.id, { active: !item.active });
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_erp_failed"));
    }
  }

  async function saveThreshold(event: FormEvent) {
    event.preventDefault();
    try {
      await configApi.setThreshold(thresholdValue);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("threshold_failed"));
    }
  }

  async function addScaleLevel(event: FormEvent) {
    event.preventDefault();
    try {
      await configApi.createScaleLevel({
        level: newLevel.level,
        labelEn: newLevel.labelEn.trim(),
        labelFr: newLevel.labelFr.trim(),
      });
      setNewLevel({ level: newLevel.level + 1, labelEn: "", labelFr: "" });
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("create_failed"));
    }
  }

  async function downloadTemplate(kind: "erp" | "scale" | "threshold") {
    try {
      const blob =
        kind === "erp"
          ? await applicationApi.downloadTemplate()
          : kind === "scale"
            ? await configApi.downloadScaleTemplate()
            : await configApi.downloadThresholdTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `skillmap-${kind}-template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  async function runImport(
    kind: "erp" | "scale" | "threshold",
    file: File | null,
    clear: () => void
  ) {
    if (!file) {
      setError(t("choose_xlsx"));
      return;
    }
    setError(null);
    setImportMsg(null);
    try {
      const report =
        kind === "erp"
          ? await applicationApi.importBulk(file)
          : kind === "scale"
            ? await configApi.importScaleBulk(file)
            : await configApi.importThresholdBulk(file);
      setImportMsg(
        `${kind}: ${t("created_n")} ${report.created}, ${t("updated_n")} ${report.updated}, ${t("errors_n")} ${report.errors.length}` +
          (report.errors[0] ? ` — ${report.errors[0].message}` : "")
      );
      clear();
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("import_failed"));
    }
  }

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("erp_scale_title")}</h1>
        </div>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}
      {importMsg ? (
        <div className="sm-alert" role="status" style={{ marginBottom: 12 }}>
          {importMsg}
        </div>
      ) : null}

      <section className="sm-section">
        <h2>{t("erp_list")}</h2>
        <div className="sm-form-actions" style={{ marginBottom: 8 }}>
          <button type="button" className="sm-btn sm-btn-secondary" onClick={() => void downloadTemplate("erp")}>
            ⬇ {t("download_excel_template")}
          </button>
        </div>
        <form
          className="sm-form-actions"
          style={{ marginBottom: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            void runImport("erp", applicationFile, () => setApplicationFile(null));
          }}
        >
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setApplicationFile(e.target.files?.[0] ?? null)}
          />
          <button type="submit" className="sm-btn sm-btn-ghost">
            {t("import_excel_erp")}
          </button>
        </form>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {applications.map((item) => (
            <li
              key={item.id}
              className="sm-toolbar"
              style={{
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: "1px solid var(--sm-border)",
              }}
            >
              <span>
                {item.label}{" "}
                <span className="sm-muted">
                  ({item.active ? t("erp_active") : t("erp_inactive")}, {t("sort_order")}{" "}
                  {item.sortOrder})
                </span>
              </span>
              <button
                type="button"
                className="sm-btn sm-btn-primary sm-btn-sm"
                onClick={() => void toggleApplication(item)}
              >
                {item.active ? t("deactivate") : t("activate")}
              </button>
            </li>
          ))}
        </ul>
        <form onSubmit={addApplication} className="sm-form-actions" style={{ marginTop: 12 }}>
          <input
            required
            className="sm-input"
            placeholder={t("new_erp")}
            value={newApplicationLabel}
            onChange={(e) => setNewApplicationLabel(e.target.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <button type="submit" className="sm-btn sm-btn-primary">
            {t("add")}
          </button>
        </form>
      </section>

      {/* ── Environnements ───────────────────────────────────────────── */}
      <section className="sm-section">
        <h2>{t("environments_title")}</h2>
        <p className="sm-muted" style={{ marginBottom: 12 }}>
          {t("environments_desc")}
        </p>

        {/* Formulaire d'ajout — toujours visible en premier */}
        <form onSubmit={(e) => void addEnvironment(e)} className="sm-form-actions" style={{ marginBottom: 16 }}>
          <select
            required
            className="sm-input"
            value={newEnvAppId}
            onChange={(e) => setNewEnvAppId(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value="">— {t("select_application")} —</option>
            {applications.filter(a => a.active).map(a => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
          <input
            required
            className="sm-input"
            placeholder={t("new_environment_label")}
            value={newEnvLabel}
            onChange={(e) => setNewEnvLabel(e.target.value)}
            style={{ flex: 1, maxWidth: 260 }}
          />
          <button type="submit" className="sm-btn sm-btn-primary">
            + {t("add")}
          </button>
        </form>

        {/* Liste groupée par application */}
        {environments.length === 0 ? (
          <p className="sm-muted" style={{ fontStyle: "italic", fontSize: "0.9em" }}>
            {t("no_environments_yet")}
          </p>
        ) : (
          applications.filter(a => environments.some(e => e.applicationId === a.id)).map((app) => (
            <div key={app.id} style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: "0.95rem", marginBottom: 6, color: "#1e40af" }}>📦 {app.label}</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {environments.filter(e => e.applicationId === app.id).map((env) => (
                  <li
                    key={env.id}
                    className="sm-toolbar"
                    style={{ justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--sm-border)" }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        background: env.active ? "#dbeafe" : "#f1f5f9",
                        color: env.active ? "#1d4ed8" : "#94a3b8",
                        padding: "2px 10px", borderRadius: 12, fontWeight: 600, fontSize: "0.9em",
                      }}>
                        {env.label}
                      </span>
                      <span className="sm-muted" style={{ fontSize: "0.82em" }}>
                        {env.active ? t("erp_active") : t("erp_inactive")}
                      </span>
                    </span>
                    <div className="sm-toolbar" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="sm-btn sm-btn-primary sm-btn-sm"
                        onClick={() => void toggleEnvironment(env)}
                      >
                        {env.active ? t("deactivate") : t("activate")}
                      </button>
                      <button
                        type="button"
                        className="sm-btn sm-btn-ghost sm-btn-sm"
                        onClick={() => void deleteEnvironment(env)}
                      >
                        {t("delete")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>

      <section className="sm-section">
        <h2>{t("skill_scale")}</h2>
        <div className="sm-form-actions" style={{ marginBottom: 8 }}>
          <button type="button" className="sm-btn sm-btn-secondary" onClick={() => void downloadTemplate("scale")}>
            ⬇ {t("download_excel_template")}
          </button>
        </div>
        <form
          className="sm-form-actions"
          style={{ marginBottom: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            void runImport("scale", scaleFile, () => setScaleFile(null));
          }}
        >
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setScaleFile(e.target.files?.[0] ?? null)}
          />
          <button type="submit" className="sm-btn sm-btn-ghost">
            {t("import_excel_scale")}
          </button>
        </form>
        <div className="sm-card">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("level")}</th>
                <th>{t("label_en")}</th>
                <th>{t("label_fr")}</th>
                <th>{t("active_from")}</th>
              </tr>
            </thead>
            <tbody>
              {scale.map((s) => (
                <tr key={s.id}>
                  <td>{s.level}</td>
                  <td>{s.labelEn}</td>
                  <td>{s.labelFr}</td>
                  <td>{s.activeFrom}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={addScaleLevel} className="sm-form-actions" style={{ marginTop: 12 }}>
          <input
            type="number"
            className="sm-input"
            min={0}
            max={10}
            value={newLevel.level}
            onChange={(e) => setNewLevel({ ...newLevel, level: Number(e.target.value) })}
            style={{ maxWidth: 80 }}
          />
          <input
            required
            className="sm-input"
            placeholder={t("label_en")}
            value={newLevel.labelEn}
            onChange={(e) => setNewLevel({ ...newLevel, labelEn: e.target.value })}
          />
          <input
            required
            className="sm-input"
            placeholder={t("label_fr")}
            value={newLevel.labelFr}
            onChange={(e) => setNewLevel({ ...newLevel, labelFr: e.target.value })}
          />
          <button type="submit" className="sm-btn sm-btn-primary">
            {t("add_level")}
          </button>
        </form>
      </section>

      <section className="sm-section">
        <h2>{t("target_threshold")}</h2>
        <div className="sm-form-actions" style={{ marginBottom: 8 }}>
          <button type="button" className="sm-btn sm-btn-secondary" onClick={() => void downloadTemplate("threshold")}>
            ⬇ {t("download_excel_template")}
          </button>
        </div>
        <form
          className="sm-form-actions"
          style={{ marginBottom: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            void runImport("threshold", thrFile, () => setThrFile(null));
          }}
        >
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setThrFile(e.target.files?.[0] ?? null)}
          />
          <button type="submit" className="sm-btn sm-btn-ghost">
            {t("import_excel_threshold")}
          </button>
        </form>
        <p>
          {t("current")} :{" "}
          <strong>{threshold ? threshold.value : t("undefined_value")}</strong>
          {threshold ? (
            <span className="sm-muted">
              {" "}
              ({t("set_on")} {new Date(threshold.setAt).toLocaleString()})
            </span>
          ) : null}
        </p>
        <form onSubmit={saveThreshold} className="sm-form-actions">
          <input
            type="number"
            className="sm-input"
            min={0}
            max={10}
            value={thresholdValue}
            onChange={(e) => setThresholdValue(Number(e.target.value))}
            style={{ maxWidth: 80 }}
          />
          <button type="submit" className="sm-btn sm-btn-primary">
            {t("save_threshold")}
          </button>
        </form>
      </section>
    </div>
  );
}
